import React, {useContext} from 'react';
import {uuid} from '../../../utils/utils';
import {PsfTemplateEditorProps} from '../../../@types/editor-props';
import SplitPane, {SashContent} from 'split-pane-react';
import {Box, Button, Stack} from '@mui/material';
import {ResourceType} from '../../../@types/resource-type';
import {psfCategoryTreeItems, replaceNameInPath} from '../../../utils/psf-template-utils';
import Typography from '@mui/material/Typography';
import {PsfCategoryTreeView} from '../modals/PsfCategoryTreeView.component';
import {hasRightToEdit} from '../../../utils/role-to-rights';
import AddIcon from '@mui/icons-material/Add';
import {UserContext} from '../../../context/userContext';
import AddCategoryModal from '../modals/AddCategory.component';
import {PsfCategory} from '../../../@types/psfTemplate';
import PsfTemplateItemsEditor from './PsfTemplateItemsEditor';
import {TreeViewItem} from '../../../@types/tree-view-item';

export default function PsfTemplateEditor(props: PsfTemplateEditorProps): React.ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { psfTemplate, categories, updateCategories, handleDeleteCategory, updateTab, isActive } = props;
    const uuidRef = React.useRef(uuid());
    const [panelSizePercent, setPanelSizePercent] = React.useState<(number | string)[]>(['28%', '72%']);
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [category, setCategory] = React.useState<PsfCategory|undefined>(undefined);
    const [addCategory, setAddCategory] = React.useState<boolean>(false);
    const [readOnly, setReadOnly] = React.useState<boolean>(false);
    const { user } = useContext(UserContext);
    const [nodes, setNodes] = React.useState<TreeViewItem[]>([]);

    React.useEffect(() => {
        if (psfTemplate) {
            setReadOnly(psfTemplate.id !== undefined && !hasRightToEdit({objectType: ResourceType.PERFORMANCE_SHAPING_FACTORS, object: psfTemplate}, user?.superUser));
        } else {
            setReadOnly(false);
        }
    }, [psfTemplate])

    React.useEffect(() => {
        setNodes(psfCategoryTreeItems(categories));
    }, [categories]);

    const onClickCategory = (event: any): void => {
        setCategory(categories[+event.target.id]);
    }

    const onClickNew = (): void => { setAddCategory(true); }

    const handleAddCategory = (parent: PsfCategory|null, category: string): void => {
        categories.push({ index: categories.length, name: category, path: parent === null ? category : parent.path + '::' + category });
        updateCategories(Object.assign([], categories));
        setAddCategory(false);
    }

    const updateCategoryWithNewName = (category: PsfCategory): void => {
        const oldName = categories[category.index].name;
        const oldPath = categories[category.index].path;
        categories[category.index].name = category.name;
        updateCategories(categories.map((cat) => {
            return cat.path.startsWith(oldPath) ? {
                ...cat,
                path: replaceNameInPath(cat.path, oldName, category.name)
            } : cat;
        }));
    }

    const updateCategoryItems = (category: PsfCategory): void => {
        categories[category.index].psfTemplateItems = Object.assign([], category.psfTemplateItems);
        updateCategories(categories);
    }

    // @ts-ignore
    return (<>
        <Box sx={{height: '100%', p: 1}}>
            <div id={uuidRef.current} style={{ width: '100vw', height: 'calc(100vh - 250px)', maxWidth: '100%', maxHeight: '100%' }}>
                <SplitPane
                    split="vertical"
                    sizes={panelSizePercent}
                    onChange={setPanelSizePercent}
                    sashRender={(index: number, active: boolean): React.ReactNode => (
                        <SashContent
                            className={active ? 'action-sash-wrap' : 'hidden-sash'}
                        >
                        </SashContent>
                    )}
                >
                    <Box sx={{ display: 'flex', overflowY: 'auto', justifyContent: 'space-between', height: '100%', width: '100%', backgroundColor: '#FAFEFE' }}>
                        <Stack sx={{width: '100%', overflowX: 'hidden', p: 0.8}}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                                <Typography variant='h6' color='primary' gutterBottom>Categories</Typography>
                                {!readOnly && <Button
                                    variant='contained'
                                    title='New Category'
                                    size='small'
                                    component='button'
                                    onClick={onClickNew}
                                    startIcon={<AddIcon />}
                                >Add</Button>
                                }
                            </Box>
                            <PsfCategoryTreeView
                                exploreMode={readOnly}
                                nodes={nodes}
                                onClickItem={onClickCategory}
                                expanded={expanded}
                                onNodeToggle={(_event: React.SyntheticEvent, nodeIds: string[]): void => {
                                    setExpanded(nodeIds);
                                }}
                                addSubCategory={handleAddCategory}
                                renameCategory={updateCategoryWithNewName}
                                deleteCategory={handleDeleteCategory}
                            />
                        </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', p: 0.5, pr: 0, margin: '0 10px' }}>
                        { category && <PsfTemplateItemsEditor categoryIndex={category?.index} psfCategories={categories} updateCategory={updateCategoryItems} isActive={true} updateTab={updateTab} />  }
                        { category === undefined && <Stack>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>The influence layer provides a means where factors that are not direct failures, but which contributed to overall system performance can be captured.</Typography>
                            <hr/>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>This interface allows you to create Performance Shaping Factors (PSFs) which can be linked to base events during occurrence mapping.</Typography>
                            <hr/>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>Click on a category on the left to start creating Performance Shaping Factors items.</Typography>
                            <hr/>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>Select the applicable event sequences on the right to link.</Typography>
                        </Stack> }
                    </Box>
                </SplitPane>
            </div>
        </Box>
        <AddCategoryModal open={addCategory} title={'Add Category'} categories={categories} onClose={(): void => { setAddCategory(false);}}
            onConfirm={handleAddCategory} />
    </>);
}

